import type { Change } from 'diff';
import { diffJson, diffLines } from 'diff';

import type { DiffStyles, ElementState } from './types';

export const createElementClassNames = (styles: DiffStyles): Record<ElementState, string> => ({
  added: styles.added,
  removed: styles.removed,
  unchanged: styles.unchanged,
});

export const elementDataAttributes: Record<ElementState, string> = {
  added: 'line-added',
  removed: 'line-removed',
  unchanged: 'line-unchanged',
};

/*
 * Diffs two strings using the `diffLines` algorithm, then splits each Change
 * into one Change per line so that the renderer can track line numbers and
 * highlight word-level sub-diffs within individual lines.
 *
 * Adjacent removed/added blocks are interleaved so that corresponding lines
 * are paired (removed[0]↔added[0], removed[1]↔added[1], …). Without this,
 * the renderer would only sub-diff the boundary pair (last-removed ↔
 * first-added), which is semantically wrong.
 */
export const diffByLine = (original: string, compare: string) => {
  // Normalize trailing newlines so that `diffLines` treats the last line the
  // same as every other line.  Without this, "line 2" (no newline) is seen as
  // different from "line 2\n", which causes phantom removals/additions when a
  // line is appended at the end.  Skip empty strings — appending '\n' would
  // create a phantom empty line in the diff.
  const normalizedOriginal = original === '' || original.endsWith('\n') ? original : `${original}\n`;
  const normalizedCompare = compare === '' || compare.endsWith('\n') ? compare : `${compare}\n`;
  const diff = diffLines(normalizedOriginal, normalizedCompare);

  const perLine = diff.flatMap((change) => {
    const lines = change.value.split('\n');
    if (lines.length > 1 && lines[lines.length - 1] === '') {
      lines.pop();
    }
    return lines.map((line) => ({
      ...change,
      value: line,
      count: 1,
    }));
  });

  return interleaveRemovedAdded(perLine);
};

/**
 * Interleaves adjacent blocks of removed and added changes so that
 * corresponding lines are next to each other. This lets the renderers
 * run `diffWords` on the correct pairs when `includeSubDiffs` is enabled.
 */
function interleaveRemovedAdded(changes: Change[]): Change[] {
  const result: Change[] = [];
  let i = 0;

  while (i < changes.length) {
    if (!changes[i].removed) {
      result.push(changes[i]);
      i++;
      continue;
    }

    const removedStart = i;
    while (i < changes.length && changes[i].removed) {
      i++;
    }
    const removed = changes.slice(removedStart, i);

    if (i < changes.length && changes[i].added) {
      const addedStart = i;
      while (i < changes.length && changes[i].added) {
        i++;
      }
      const added = changes.slice(addedStart, i);

      const maxLen = Math.max(removed.length, added.length);
      for (let j = 0; j < maxLen; j++) {
        if (j < removed.length) {
          result.push(removed[j]);
        }
        if (j < added.length) {
          result.push(added[j]);
        }
      }
    } else {
      result.push(...removed);
    }
  }

  return result;
}

// Using the diffJson function results in a ~ significantly ~ better diff, but it doesn't
// split unchanged sections into multiple lines. We'll do a little post-processing here
// to ensure that unchanged sections are split across multiple lines so that our line numbering
// can function as expected when line numbers are required
export const diffJSONByLine = (original: string, compare: string) => {
  const diff = diffJson(original, compare);
  // Map through the diff and split unchanged lines into multiple Change objects that'll
  // be flattened by flatMap() into a Change[]
  const unchangedSplitByLine = diff
    .flatMap((change) => {
      if (change.added || change.removed) {
        return change;
      }
      // split the unchanged line into multiple Change objects
      const unchangedLines = change.value.split('\n');
      return unchangedLines.map((line) => ({ ...change, value: line }));
    })
    .filter((change) => change.value.trim());

  return unchangedSplitByLine;
};

/** Mirrors the renderers' numbering: a removed line and the added line replacing it share a number. */
export function maxRenderedLineNumber(diff: Change[]): number {
  if (diff.length === 0) {
    return 0;
  }

  let lineNumber = 1;
  for (let i = 0; i < diff.length - 1; i++) {
    if (!(diff[i].removed && diff[i + 1].added)) {
      lineNumber++;
    }
  }

  return lineNumber;
}

/**
 * Extra gutter width the diff's largest line number needs beyond the two digits reserved by default.
 * Collapsible diffs number every line, so they can run higher than the unified renderer.
 */
export function lineNumberGutterExtraWidth(diff: Change[], isCollapsible: boolean): string {
  const largest = isCollapsible
    ? diff.reduce((total, change) => total + (change.count ?? 1), 0)
    : maxRenderedLineNumber(diff);

  return `${Math.max(0, String(largest).length - 2)}ch`;
}

// Convenience method for stringifying objects for diffing in the format they need
// to display well
export function stringifyForDiff(obj: object) {
  return JSON.stringify(obj, null, 2);
}
